import { buildURL } from "app/client/lib/urlUtils";
import { getMainOrgUrl } from "app/client/models/gristUrlState";
import { BaseAPI } from "app/common/BaseAPI";
import { OAuthClientPayload, OAuthClientWithSecret } from "app/common/SignInWithGristAPI";

/**
 * API for performing actions related to "Sign in with getgrist.com".
 */
export class SignInWithGristAPI extends BaseAPI {
  /**
   * The URL prefix for OAuth interaction endpoints.
   */
  private _interactionUrl: string;

  /**
   * The URL prefix for OAuth client endpoints.
   */
  private _clientUrl: string;

  constructor() {
    super();

    const baseUrl = getMainOrgUrl().replace(/\/$/, "");
    this._interactionUrl = baseUrl + "/oauth/interaction";
    this._clientUrl = baseUrl + "/api/oauth/clients";
  }

  /**
   * Redirects to the login page (`/login`) to add a new account.
   *
   * On successful login, the user will be redirected back to the OAuth interaction
   * page with the specified `cid`.
   */
  public redirectToLogin(cid: string, clientId: string): void {
    const url = buildURL("/login", {
      hash: null,
      searchParams: new URLSearchParams({
        cid,
        client_id: clientId,
      }),
    });
    window.location.assign(url.href);
  }

  /**
   * Redirects to the select account page (`/oauth/interaction/:cid/select-account`).
   */
  public redirectToSelectAccount(cid: string): void {
    window.location.assign(`${this._interactionUrl}/${cid}/select-account`);
  }

  /**
   * Returns the URL of the consent page (`/oauth/interaction/:cid/consent`).
   *
   * This URL is used to request the consent page for a user selected in the "select_account"
   * prompt. The caller must specify which user was selected in the `authuser` form data key,
   * which is the 0-based index of the selected user.
   *
   * If a user has already allowed access to the requested scopes, a request to this URL will
   * not retrieve the consent page, but will instead redirect to the OAuth client redirect URI
   * with an authorization code.
   */
  public getConsentUrl(cid: string): string {
    return `${this._interactionUrl}/${cid}/consent`;
  }

  /**
   * Allows or denies the requested scopes on the consent form page (`/oauth/interaction/:cid/consent`).
   *
   * Returns a redirect URL that redirects to the OAuth client with an authorization code (if allowed)
   * or an error (if denied).
   */
  public consent(cid: string, decision: "allow" | "deny"): Promise<{ redirectUrl: string }> {
    return this.requestJson(`${this._interactionUrl}/${cid}/consent`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    });
  }

  /**
   * Registers a new OAuth client with the specified `params`.
   *
   * Returns the registered client with the secret exposed.
   */
  public registerClient(params: OAuthClientPayload): Promise<OAuthClientWithSecret> {
    return this.requestJson(this._clientUrl, {
      method: "POST",
      body: JSON.stringify(params),
    });
  }
}
